import { Controller } from "@hotwired/stimulus";

/**
 * Moves the caret through the boxes of a one-time code.
 *
 * The boxes are real inputs that post on their own, so this only spreads a
 * pasted or autofilled code over them and keeps typing going forwards.
 */
export default class OneTimeCodeController extends Controller<HTMLElement> {
  static override targets = ["box"];
  declare readonly boxTargets: HTMLInputElement[];

  spread(event: Event): void {
    const box = event.target as HTMLInputElement;
    this.write(box, box.value);
  }

  paste(event: ClipboardEvent): void {
    event.preventDefault();
    this.write(
      event.target as HTMLInputElement,
      event.clipboardData?.getData("text") ?? "",
    );
  }

  navigate(event: KeyboardEvent): void {
    const box = event.target as HTMLInputElement;
    const index = this.boxTargets.indexOf(box);

    if (event.key === "Backspace" && box.value === "" && index > 0) {
      event.preventDefault();
      const previous = this.boxTargets[index - 1];
      if (previous === undefined) {
        return;
      }
      previous.value = "";
      previous.focus();
    } else if (event.key === "ArrowLeft" && index > 0) {
      event.preventDefault();
      this.focus(index - 1);
    } else if (event.key === "ArrowRight") {
      event.preventDefault();
      this.focus(index + 1);
    }
  }

  /** Puts one character per box, starting at the box the text arrived in. */
  private write(box: HTMLInputElement, text: string): void {
    const start = this.boxTargets.indexOf(box);
    const characters = [...text].filter((character) => !/\s/.test(character));

    for (const [offset, character] of characters.entries()) {
      const target = this.boxTargets[start + offset];
      if (target === undefined) {
        break;
      }
      target.value = character;
    }

    this.focus(start + Math.max(characters.length, 1));
  }

  private focus(index: number): void {
    const box = this.boxTargets[Math.min(index, this.boxTargets.length - 1)];
    box?.focus();
    box?.select();
  }
}
